import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { apiClient } from '../api/client';
import { hapticFeedback, isTelegramWebApp } from '../utils/telegram';
import { GlowCard } from '../components/ui/glow-card';
import { PremiumButton } from '../components/ui/PremiumButton';
import { useI18n } from '../i18n';

interface TrainingPageProps {
  onBack?: () => void;
}

export const TrainingPage: React.FC<TrainingPageProps> = ({ onBack }) => {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const validatePhone = (phoneNumber: string): boolean => {
    // Убираем все нецифровые символы
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    // Проверяем, что осталось минимум 10 цифр (7 + 10 для российского номера)
    return digitsOnly.length >= 10;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Валидация
    if (!name.trim()) {
      setErrorMessage(t('trainingNameRequired'));
      return;
    }

    if (!phone.trim()) {
      setErrorMessage(t('trainingPhoneRequired'));
      return;
    }

    if (!validatePhone(phone)) {
      setErrorMessage(t('trainingPhoneInvalid'));
      return;
    }

    setIsSubmitting(true);
    if (isTelegramWebApp()) hapticFeedback('heavy');

    try {
      // Отправляем заявку через backend API
      const result = await apiClient.applyForTraining({
        name: name.trim(),
        phone: phone.trim(),
        source: 'miniapp_training',
      });

      if (result.success) {
        setSuccessMessage(t('trainingSuccess'));
        if (isTelegramWebApp()) {
          // @ts-ignore
          window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('medium');
        }
        // Очищаем форму
        setName('');
        setPhone('');
      } else {
        setErrorMessage(t('trainingError'));
      }
    } catch (error: unknown) {
      setErrorMessage(t('trainingError'));
      
      if (isTelegramWebApp()) {
        // @ts-ignore
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header - Apple Typography Only */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-3 px-6"
      >
        <h1 
          className="text-[32px] leading-[1.1] mb-2"
          style={{
            fontWeight: 500,
            letterSpacing: '-0.02em',
            color: 'rgba(255, 255, 255, 0.95)'
          }}
        >
          {t('trainingTitle')}
        </h1>
        <h2 
          className="text-[18px] leading-[1.35] max-w-[280px] mx-auto"
          style={{
            fontWeight: 400,
            letterSpacing: '-0.01em',
            color: 'rgba(255, 255, 255, 0.72)'
          }}
        >
          {t('training')}
        </h2>
        <p 
          className="text-[15px] leading-[1.45] mt-5 max-w-[300px] mx-auto"
          style={{
            fontWeight: 400,
            letterSpacing: '-0.005em',
            color: 'rgba(255, 255, 255, 0.55)'
          }}
        >
          {t('becomeBarber')}
        </p>
      </motion.div>

      {/* Form Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <GlowCard glowColor="gold" customSize>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Input */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                {t('yourName')}
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('yourName')}
                className="w-full px-4 py-3 rounded-xl text-white placeholder-white/40 focus:outline-none transition-all"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = '0 3px 10px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.08)';
                  e.target.style.border = '1px solid rgba(255,255,255,0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.2)';
                  e.target.style.border = '1px solid rgba(255,255,255,0.08)';
                }}
                disabled={isSubmitting}
                required
              />
            </div>

            {/* Phone Input */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-white mb-2">
                {t('phone')}
              </label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+7 (___) ___-__-__"
                className="w-full px-4 py-3 rounded-xl text-white placeholder-white/40 focus:outline-none transition-all"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = '0 3px 10px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.08)';
                  e.target.style.border = '1px solid rgba(255,255,255,0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.2)';
                  e.target.style.border = '1px solid rgba(255,255,255,0.08)';
                }}
                disabled={isSubmitting}
                required
              />
            </div>

            {/* Submit Button */}
            <PremiumButton
              fullWidth
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('submitting') : t('submitApplication')}
            </PremiumButton>

            {/* Success Message */}
            {successMessage && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-green-400 text-center mt-2"
              >
                {successMessage}
              </motion.p>
            )}

            {/* Error Message */}
            {errorMessage && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-red-400 text-center mt-2"
              >
                {errorMessage}
              </motion.p>
            )}

            {/* Privacy Notice */}
            <p className="text-xs text-white/60 text-center mt-4">
              {t('consent')}
            </p>
          </form>
        </GlowCard>
      </motion.div>

      {/* Back Button */}
      {onBack && (
        <PremiumButton
          fullWidth
          variant="secondary"
          onClick={onBack}
        >
          ← {t('back')}
        </PremiumButton>
      )}
    </div>
  );
};


